// Product recommendation for the hardware-first quiz.
//
// Ported from fi-quizz: the rule data mirrors `fi-quizz/src/rules/rules.generated.ts`,
// which is compiled from `fi-quizz/rules/recommendation.md` (the human-editable
// source of truth, owned by product). The evaluator mirrors
// `fi-quizz/src/rules/engine.ts`.
//
// The rule DSL is kept rather than collapsed into two if-statements so a change
// to recommendation.md is a readable diff here. SOURCE_HASH is the only drift
// detector: when it stops matching the hash in fi-quizz's generated file, the
// rules have moved and this table needs re-porting.

import { ImportanceId, QuizAnswers, SizeId } from './steps';

/** sha256 of fi-quizz/rules/recommendation.md at the time this was ported. */
export const SOURCE_HASH = 'f314ea7fbaa0efb8a25ca09fb0fbf4644d0cb69406864a0bf55c1a8504344a10';

export type ProductId = 'mini' | 'series3plus';

export const ALL_PRODUCTS: readonly ProductId[] = ['mini', 'series3plus'];

/** The `device` key the pick-your-fi shop selects on, for each product the quiz can land on. */
export const PRODUCT_DEVICE_KEY: Record<ProductId, 'mini' | 's3'> = {
  mini: 'mini',
  series3plus: 's3',
};

export type Condition =
  | { fact: 'species'; equals: 'dog' | 'cat' }
  | { fact: 'size'; equals: SizeId }
  | { fact: 'size'; in: readonly SizeId[] }
  | { fact: 'importance'; includes: ImportanceId }
  | { fact: 'importance'; includesAny: readonly ImportanceId[] }
  | { all: readonly Condition[] }
  | { any: readonly Condition[] }
  | { not: Condition };

/** Absolute exclusion: when the condition matches, these products are out. */
export interface GateRule {
  id: string;
  when: Condition;
  exclude: readonly ProductId[];
}

/** Weighted lean: when the condition matches, add points. */
export interface ScoreRule {
  id: string;
  when: Condition;
  scores: Partial<Record<ProductId, number>>;
}

export interface RuleSet {
  gates: readonly GateRule[];
  scores: readonly ScoreRule[];
  /** Default lean applied before any ScoreRule fires. */
  baseScores: Record<ProductId, number>;
  /** Deterministic ordering for equal scores; lists every product. */
  tieBreak: readonly ProductId[];
}

export const RULES: RuleSet = {
  gates: [
    // Never fires in this flow, which always answers `dog`. Kept so the table
    // stays a literal copy of recommendation.md.
    { id: 'cats-mini-only', when: { fact: 'species', equals: 'cat' }, exclude: ['series3plus'] },
  ],
  scores: [
    {
      id: 'small-size-mini',
      when: {
        all: [
          { fact: 'species', equals: 'dog' },
          { fact: 'size', in: ['extra_small', 'small'] },
        ],
      },
      scores: { mini: 5 },
    },
    {
      id: 'importance-attach-mini',
      when: { fact: 'importance', includes: 'attach' },
      scores: { mini: 5 },
    },
  ],
  baseScores: { mini: 0, series3plus: 1 },
  tieBreak: ['series3plus', 'mini'],
};

export interface ProductScore {
  productId: ProductId;
  score: number;
  eligible: boolean;
}

export interface RecommendationResult {
  best: ProductId;
  /** Every product with its score, for analytics and debugging. */
  all: ProductScore[];
  /** Gate rule ids that fired, for analytics and debugging. */
  firedGates: string[];
}

/**
 * Series 3+ is the full-featured collar and the product we can always fall back
 * to when gates would otherwise exclude everything.
 */
const FALLBACK_PRODUCT: ProductId = 'series3plus';

export function evaluateCondition(condition: Condition, answers: QuizAnswers): boolean {
  if ('all' in condition) {
    return condition.all.every((c) => evaluateCondition(c, answers));
  }
  if ('any' in condition) {
    return condition.any.some((c) => evaluateCondition(c, answers));
  }
  if ('not' in condition) {
    return !evaluateCondition(condition.not, answers);
  }

  switch (condition.fact) {
    case 'species':
      return answers.species === condition.equals;
    case 'size':
      // An unanswered step matches nothing rather than throwing: the sheet can
      // reach the result with a skipped step if the flow ever allows it.
      if (answers.size === null) {
        return false;
      }
      return 'equals' in condition ? answers.size === condition.equals : condition.in.includes(answers.size);
    case 'importance':
      return 'includes' in condition
        ? answers.importance.includes(condition.includes)
        : condition.includesAny.some((i) => answers.importance.includes(i));
  }
}

export function recommend(answers: QuizAnswers, rules: RuleSet = RULES): RecommendationResult {
  const excluded = new Set<ProductId>();
  const firedGates: string[] = [];

  for (const gate of rules.gates) {
    if (evaluateCondition(gate.when, answers)) {
      firedGates.push(gate.id);
      gate.exclude.forEach((product) => excluded.add(product));
    }
  }

  const scores = new Map<ProductId, number>(ALL_PRODUCTS.map((p) => [p, rules.baseScores[p] ?? 0]));

  rules.scores
    .filter((rule) => evaluateCondition(rule.when, answers))
    .forEach((rule) => {
      ALL_PRODUCTS.forEach((product) => {
        const points = rule.scores[product];
        if (points !== undefined) {
          scores.set(product, (scores.get(product) ?? 0) + points);
        }
      });
    });

  const tieBreakIndex = (p: ProductId) => {
    const i = rules.tieBreak.indexOf(p);
    return i === -1 ? rules.tieBreak.length : i;
  };

  const ranked: ProductScore[] = ALL_PRODUCTS.map((productId) => ({
    productId,
    score: scores.get(productId) ?? 0,
    eligible: !excluded.has(productId),
  })).sort((a, b) => b.score - a.score || tieBreakIndex(a.productId) - tieBreakIndex(b.productId));

  // Contradictory gates could exclude everything; never strand the visitor
  // without a recommendation.
  const best = ranked.find((r) => r.eligible)?.productId ?? FALLBACK_PRODUCT;

  return { best, all: ranked, firedGates };
}
